// Sezon seçimi — tüm ekranlar (Teklifler, Raporlar, Gösterge Paneli, Takvim) bu seçime göre süzülür.
//
//   activeSeason   → Ayarlar > Sezon'da seçili sezon (settings kategorisi 'general').
//                    YENİ teklifler bu sezonla kaydedilir. Ayar yoksa bugünün tarihinden türetilir.
//   selectedSeason → üstteki seçicide görüntülenen sezon ('all' = Tüm Sezonlar).
//                    Tarayıcıda saklanır (localStorage), ilk açılışta aktif sezon gelir.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { API_URL } from '../utils/api';
import { ALL_SEASONS, isValidSeason, seasonFromDate } from '../utils/season';

const STORAGE_KEY = 'ramada_selected_season';

const SeasonContext = createContext({
  activeSeason: '',
  activeSeasonSaved: false,
  selectedSeason: ALL_SEASONS,
  setSelectedSeason: () => {},
  refreshActiveSeason: () => {},
  retryActiveSeasonIfNeeded: () => {},
});

export function useSeason() {
  const ctx = useContext(SeasonContext);
  const { retryActiveSeasonIfNeeded } = ctx;
  // Ayar giriş öncesi okunamadıysa (401) girişten sonra ilk kullanan ekranda tekrar okunur; aksi halde hiçbir şey yapmaz
  useEffect(() => { retryActiveSeasonIfNeeded?.(); }, [retryActiveSeasonIfNeeded]);
  return ctx;
}

const readStored = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === ALL_SEASONS || isValidSeason(v) ? v : '';
  } catch {
    return ''; // gizli sekmede localStorage erişimi hata verebilir
  }
};

export function SeasonProvider({ children }) {
  const [activeSeason, setActiveSeason] = useState('');
  // Ayarlar'da gerçekten kayıtlı bir sezon var mı (yoksa activeSeason bugünün tarihinden türetilmiştir)
  const [activeSeasonSaved, setActiveSeasonSaved] = useState(false);
  // İlk açılışta "Tüm Sezonlar": kimse boş liste görmesin, seçim tarayıcıda saklanır
  const [selectedSeason, setSelectedSeasonState] = useState(() => readStored() || ALL_SEASONS);

  const setSelectedSeason = useCallback((season) => {
    const next = season === ALL_SEASONS || isValidSeason(season) ? season : ALL_SEASONS;
    setSelectedSeasonState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* yoksay */ }
  }, []);

  // Ayar okunamadıysa (giriş öncesi 401, sunucu hatası, ağ) sonraki ekran açılışında bir kez daha denenir
  const retryNeededRef = useRef(false);

  const refreshActiveSeason = useCallback(async () => {
    let season = '';
    let failed = false;
    try {
      const res = await fetch(`${API_URL}/settings/general`);
      if (res.ok) {
        let data = null;
        try { data = await res.json(); } catch { /* JSON olmayan yanıt → varsayılan */ }
        if (data && typeof data === 'object' && isValidSeason(data.activeSeason)) season = data.activeSeason;
      } else {
        failed = true;
        // 401/403: henüz giriş yapılmamış (ayarlar artık token istiyor) — beklenen durum, sessiz geç
        if (res.status !== 401 && res.status !== 403) console.error(`Sezon ayarı okunamadı (${res.status}).`);
      }
    } catch (error) {
      failed = true; // ağ hatası: tarihten türetilene düşülür
      console.error('Sezon ayarı okunamadı:', error);
    }
    retryNeededRef.current = failed;
    setActiveSeasonSaved(!!season);
    if (!season) season = seasonFromDate(new Date()); // ayar hiç kaydedilmemişse ya da okunamadıysa
    setActiveSeason(season);
    return season;
  }, []);

  useEffect(() => { refreshActiveSeason(); }, [refreshActiveSeason]);

  // SeasonProvider AuthProvider'ın DIŞINDA: giriş ekranında ayar 401 ile okunamaz, girişten sonra sayfa yenilenmez.
  // Okuma başarısız olduysa, sezonu kullanan ilk ekran açıldığında (useSeason) bir kez daha okunur.
  const retryActiveSeasonIfNeeded = useCallback(() => {
    if (!retryNeededRef.current) return;
    retryNeededRef.current = false;
    refreshActiveSeason();
  }, [refreshActiveSeason]);

  return (
    <SeasonContext.Provider value={{ activeSeason, activeSeasonSaved, selectedSeason: selectedSeason || ALL_SEASONS, setSelectedSeason, refreshActiveSeason, retryActiveSeasonIfNeeded }}>
      {children}
    </SeasonContext.Provider>
  );
}
